/**
 * Indian currency formatting.
 *
 * Getting this wrong is an instant credibility tell to the audience this product
 * is for. "₹1,234,567" reads as foreign; "₹12,34,567" reads as native. Lakh and
 * crore are the units Indians actually think in — nobody says "1.2 million".
 */

const LAKH = 100_000
const CRORE = 10_000_000

const enIN = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })

/** Full grouped rupees: 1234567 -> "₹12,34,567". Use where precision matters. */
export function formatINRFull(value: number): string {
  if (!Number.isFinite(value)) return '—'
  const sign = value < 0 ? '-' : ''
  return `${sign}₹${enIN.format(Math.abs(Math.round(value)))}`
}

/**
 * Compact rupees for headlines, axis ticks and tooltips.
 *
 *     920            -> "₹920"
 *     45_000         -> "₹45,000"
 *     1_250_000      -> "₹12.5 L"
 *     34_500_000     -> "₹3.45 Cr"
 *
 * Significant digits scale with magnitude so a value never shows more precision
 * than the simulation supports — a projection reading "₹3.4517 Cr" is lying
 * about its own certainty.
 */
export function formatINRCompact(value: number): string {
  if (!Number.isFinite(value)) return '—'

  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)

  if (abs >= CRORE) {
    const cr = abs / CRORE
    // 1.23 Cr / 12.3 Cr / 123 Cr — three significant figures throughout.
    const decimals = cr >= 100 ? 0 : cr >= 10 ? 1 : 2
    return `${sign}₹${trimZeros(cr.toFixed(decimals))} Cr`
  }

  if (abs >= LAKH) {
    // Always one decimal, trimmed. Lakhs only ever run 1–99.99 before crores take
    // over, so this holds three significant figures across the whole band and
    // matches the crore branch. Rounding 12.5 L to "13 L" would put a 4% error on
    // screen in a projection people are trying to plan against.
    return `${sign}₹${trimZeros((abs / LAKH).toFixed(1))} L`
  }

  return `${sign}₹${enIN.format(Math.round(abs))}`
}

/**
 * Axis-tick variant: never shows decimals it doesn't need, so a tick column
 * reads "₹1 Cr / ₹2 Cr / ₹3 Cr" rather than "₹1.00 Cr / ₹2.00 Cr".
 */
export function formatINRTick(value: number): string {
  if (!Number.isFinite(value)) return ''
  if (value === 0) return '₹0'

  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (abs >= CRORE) {
    const cr = abs / CRORE
    return `${sign}₹${trimZeros(cr.toFixed(cr < 10 ? 1 : 0))} Cr`
  }
  if (abs >= LAKH) {
    const l = abs / LAKH
    return `${sign}₹${trimZeros(l.toFixed(0))} L`
  }
  return `${sign}₹${enIN.format(Math.round(abs))}`
}

/** "12.50" -> "12.5", "3.00" -> "3". Keeps ticks from looking over-precise. */
function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s
}

/** Percentages are always whole numbers here — a FIRE probability of 73.4% is false precision. */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—'
  return `${Math.round(fraction * 100)}%`
}

/**
 * Reads a rupee amount the way a person would type it.
 *
 * This is the inverse of `formatINRCompact` and it sits directly between the
 * user's thumbs and the engine, so it has to be generous about input and strict
 * about output. Someone correcting a slider on a phone will type `50k`, `1.2cr`,
 * `₹8 L`, or paste `12,34,567` straight back in. All of those mean something
 * unambiguous and all of them should work.
 *
 * Returns `null` — never `NaN`, never a guess — when the input cannot be read.
 * The caller keeps the previous value on null, so a half-typed entry never
 * blanks the chart.
 *
 *     '50000'    -> 50000        '50k'      -> 50000
 *     '50,000'   -> 50000        '1.2cr'    -> 12000000
 *     '₹8L'      -> 800000       '8 lakh'   -> 800000
 *     '2.5 Cr'   -> 25000000     ''         -> null
 */
export function parseINR(input: string): number | null {
  // Strip the currency symbol, digit grouping, and all whitespace — including
  // the non-breaking space some mobile keyboards insert.
  const cleaned = input
    .replace(/[₹,\s ]/g, '')
    .trim()
    .toLowerCase()

  if (cleaned === '') return null

  // Longest suffixes first: 'cr' must win before 'c' would, and 'lakh' before 'l'.
  const units: [RegExp, number][] = [
    [/^(-?\d*\.?\d+)crores?$/, CRORE],
    [/^(-?\d*\.?\d+)cr$/, CRORE],
    [/^(-?\d*\.?\d+)c$/, CRORE],
    [/^(-?\d*\.?\d+)lakhs?$/, LAKH],
    [/^(-?\d*\.?\d+)lacs?$/, LAKH],
    [/^(-?\d*\.?\d+)l$/, LAKH],
    [/^(-?\d*\.?\d+)k$/, 1_000],
    [/^(-?\d*\.?\d+)$/, 1],
  ]

  for (const [pattern, multiplier] of units) {
    const match = pattern.exec(cleaned)
    if (match !== null) {
      const magnitude = Number(match[1])
      if (!Number.isFinite(magnitude)) return null
      return magnitude * multiplier
    }
  }

  return null
}

/**
 * Parses, then forces the result into a range the engine can actually model.
 *
 * Typing an extra zero is a one-key mistake, and an unclamped ₹50 crore monthly
 * SIP would blow the y-axis out so far the fan collapses to a flat line against
 * the bottom of the chart — the UI would look broken rather than wrong. Clamping
 * keeps the chart legible and lets the user see their own typo.
 */
export function parseINRClamped(input: string, min: number, max: number): number | null {
  const parsed = parseINR(input)
  if (parsed === null) return null
  return Math.min(max, Math.max(min, parsed))
}

/** True when the input carried no unit — a bare number like `17` or `1.7`. */
function isBareNumber(input: string): boolean {
  return /^-?\d*\.?\d+$/.test(input.replace(/[₹,\s ]/g, '').trim())
}

/**
 * Reads a bare number in the unit the readout is already showing.
 *
 * The readout says `₹12 L`. You tap it and type `17`. You mean seventeen lakh —
 * nobody editing a field that reads "₹12 L" means seventeen rupees. But `17`
 * parses as 17, which is below every money slider's floor, so it used to clamp
 * silently to the minimum: the chart then answered a question the user never
 * asked, with no indication anything had been rejected. That produced a real
 * report of "wrong numbers" from a user who had typed a perfectly sensible thing.
 *
 * So: if the input is a BARE number (no k/L/Cr suffix) and it is implausibly
 * small for a money field, re-read it in the unit currently displayed. `17`
 * against a ₹12 L readout becomes ₹17 L; `2` against a ₹15 Cr readout becomes
 * ₹2 Cr.
 *
 * The trigger is absolute smallness, NOT "below the slider's minimum". That
 * distinction is load-bearing: `currentCorpus` has a minimum of zero, so nothing
 * is ever below it, and a bare `2` there would stay two rupees forever.
 *
 * It is equally important that the rule does NOT simply always rescale. Someone
 * looking at a ₹15 Cr corpus who types `500000` means five lakh — a perfectly
 * ordinary correction — and multiplying that by a crore would be a far worse bug
 * than the one being fixed. Hence the threshold: below ₹1,000 no one is naming
 * an amount of rupees, and at ₹1,000 or above they plainly are.
 *
 * An explicit unit is never second-guessed. `17k` means seventeen thousand and
 * is clamped normally if the slider cannot reach it.
 */
const BARE_NUMBER_CEILING = 1_000

export function parseINRInUnitOf(
  input: string,
  currentValue: number,
  min: number,
  max: number,
): number | null {
  const parsed = parseINR(input)
  if (parsed === null) return null

  if (parsed > 0 && parsed < BARE_NUMBER_CEILING && isBareNumber(input)) {
    const unit = currentValue >= CRORE ? CRORE : currentValue >= LAKH ? LAKH : 1
    const rescaled = parsed * unit
    /*
     * Only take the rescaled reading if it fits the slider WITHOUT clamping.
     *
     * Clamping it was a bug, and a worse one than the silent floor-clamp this
     * function exists to fix. On "Investing monthly" the readout shows ₹1 L and
     * the maximum is ₹10 L; someone typing `50` for a ₹50,000 SIP had it read as
     * fifty lakh, clamped to the ceiling, and committed as ₹10 L — a twentyfold
     * overstatement of their own savings rate, silently.
     *
     * Overshooting the range is the signal that the unit guess was wrong. When
     * that happens, fall through and take the number literally: ₹50 is visibly
     * not what they meant and they can correct it, where ₹10 L looks plausible
     * and is not.
     */
    if (unit > 1 && rescaled >= min && rescaled <= max) return rescaled
  }

  return Math.min(max, Math.max(min, parsed))
}
